import React, { useState } from 'react';
import { Church, GraduationCap, Heart, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { categories } from '../data/mockData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const iconMap = {
  Church: Church,
  GraduationCap: GraduationCap,
  Heart: Heart,
};

const CategoryModal = ({ isOpen, onClose, onSelectCategory }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleContinue = () => {
    if (selectedCategory) {
      setIsAnimating(true);
      setTimeout(() => {
        onSelectCategory(selectedCategory);
        setIsAnimating(false);
      }, 300);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-accent/98 border border-primary/30 backdrop-blur-xl max-w-2xl p-0 overflow-hidden [&>button]:hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-primary-foreground/70 hover:text-primary-foreground transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 pointer-events-none" />

        <div className="relative p-8">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-bold text-primary-foreground text-center">
              Elige tu Categoría de Cuidador
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/80 text-center mt-3 text-base">
              Selecciona el tipo de cuidador que mejor te describe.
            </DialogDescription>
          </DialogHeader>

          {/* Category Options */}
          <div className="grid gap-4 mb-8">
            {categories.map((category) => {
              const Icon = iconMap[category.icon];
              const isSelected = selectedCategory === category.id;

              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`relative flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 text-left ${
                    isSelected
                      ? 'bg-primary/30 border-primary shadow-[0_0_30px_hsl(var(--primary)/0.2)]'
                      : 'bg-primary-foreground/10 border-primary-foreground/20 hover:bg-primary-foreground/20 hover:border-primary-foreground/30'
                  }`}
                >
                  {/* Selection indicator */}
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-primary-foreground/50'
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-full" />
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`p-3 rounded-xl transition-all duration-300 ${
                      isSelected
                        ? 'bg-primary/40 border border-primary/50'
                        : 'bg-primary-foreground/10 border border-primary-foreground/20'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 transition-colors duration-300 ${
                        isSelected
                          ? 'text-primary-foreground'
                          : 'text-primary-foreground/80'
                      }`}
                    />
                  </div>

                  {/* Text */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-primary-foreground">
                      {category.title}
                    </h3>
                    <p className="text-primary-foreground/70 text-sm mt-1">
                      {category.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={!selectedCategory}
            className={`w-full py-6 text-lg font-semibold rounded-full transition-all duration-300 ${
              selectedCategory
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_40px_hsl(var(--primary)/0.3)]'
                : 'bg-primary/30 text-muted-foreground cursor-not-allowed'
            } ${isAnimating ? 'scale-95 opacity-50' : ''}`}
          >
            <span className="flex items-center justify-center gap-3">
              Continuar al Curso
              <ArrowRight className="w-5 h-5" />
            </span>
          </Button>

          {/* Note */}
          <p className="text-center text-primary-foreground/60 text-sm mt-4">
            Estas a un solo paso de empezar la capacitación.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryModal;
